import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { FileSubmissionsContext } from "@/components/layout/FileSubmissionsTracker";
import {
  LuBuilding2,
  LuCheck,
  LuEye,
  LuPencil,
  LuShield,
} from "react-icons/lu";
import {
  User,
  UserOrg,
  UserOrgForm,
  userMutationFunctions,
} from "@/models/user.tsx";
import { useUserDetail } from "@/queries.ts";
import { CoreDataModel } from "@/models";
import {
  RECORD_TYPE_LABELS,
  ADDITIONAL_PERMISSION_LABELS,
} from "@/components/user-management/hooks/useOrgPermissions";

const bulkBtnStyle = (active: boolean): React.CSSProperties => ({
  fontSize: "0.8rem",
  borderRadius: 6,
  padding: "5px 12px",
  border: `1px solid ${active ? "var(--color-p2)" : "#cbd5e1"}`,
  background: active ? "rgba(0,58,93,0.08)" : "#f8fafc",
  color: active ? "var(--color-p2)" : "#64748b",
  fontWeight: active ? 600 : 500,
  transition: "all 0.15s",
  cursor: "pointer",
});

type GrantAccessFormProps = {
  user: User<UserOrg>;
  isNewRecord?: boolean;
  onUserOrgChange?: (userOrg: UserOrgForm | null) => void;
  leaid: string;
  coreData: CoreDataModel;
  entityResult: any;
  childrenResult: any;
  onSaveSuccess: () => void;
  onRegisterSubmit: (fn: () => void) => void;
  onDirtyChange: (dirty: boolean) => void;
  onSubmittingChange?: (submitting: boolean) => void;
};

export function GrantAccessForm({
  user,
  isNewRecord = false,
  onUserOrgChange,
  leaid,
  coreData,
  entityResult,
  childrenResult,
  onSaveSuccess,
  onRegisterSubmit,
  onDirtyChange,
  onSubmittingChange,
}: GrantAccessFormProps) {
  const queryClient = useQueryClient();
  const { showToast } = useContext(FileSubmissionsContext);

  const entityGroup = [entityResult?.data, ...(childrenResult?.data ?? [])]
    .flat()
    .filter(Boolean);

  const securityGroups = new Set(
    entityGroup.map((x: any) => x.SecurityGroupKey)
  );
  const availableSecurityGroups = (coreData?.securityGroup ?? []).filter((x) =>
    securityGroups.has(x.ScrtyGrpKey)
  );
  const firstOrgType =
    availableSecurityGroups[0]?.ScrtyGrpKey?.toString() ?? "";

  const wrappedOnSaveSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["users", leaid] });
    queryClient.invalidateQueries({ queryKey: ["users", "0-"] });
    onSaveSuccess();
    showToast("success", "Success", "Access granted successfully.");
  };

  const userDetailResult = useUserDetail(
    leaid,
    isNewRecord ? "" : (user.userName ?? "")
  );
  const fullUser = isNewRecord ? user : (userDetailResult.data ?? user);

  const userMutateFns = userMutationFunctions(leaid, wrappedOnSaveSuccess);

  useEffect(() => {
    if (isNewRecord) return; // parent owns submitting state in new-user mode
    onSubmittingChange?.(userMutateFns.mutationState.isPending);
  }, [userMutateFns.mutationState.isPending, onSubmittingChange, isNewRecord]);

  const { handleSubmit, watch, setValue } = useForm<UserOrgForm>({
    defaultValues: { orgType: firstOrgType },
  });

  const orgTypeWatch = watch("orgType");

  useEffect(() => {
    if (firstOrgType) setValue("orgType", firstOrgType);
  }, [firstOrgType, setValue]);

  const orgRoles = useMemo(
    () =>
      (coreData?.securityRoles ?? []).filter(
        (r) => r.ScrtyGrpKey?.toString() === orgTypeWatch?.toString()
      ),
    [coreData?.securityRoles, orgTypeWatch]
  );

  const recordTypes = useMemo(() => {
    const order = Object.keys(RECORD_TYPE_LABELS);
    return [
      ...new Set(
        orgRoles
          .filter(
            (r) =>
              r.CAPSDACRoleCode.endsWith("View") ||
              r.CAPSDACRoleCode.endsWith("Edit")
          )
          .map((r) => r.CAPSDACRoleCode.replace(/View$|Edit$/, ""))
      ),
    ].sort((a, b) => {
      const ai = order.indexOf(a);
      const bi = order.indexOf(b);
      if (ai === -1 && bi === -1) return 0;
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  }, [orgRoles]);

  const additionalPermissions = useMemo(
    () =>
      orgRoles
        .filter(
          (r) =>
            !r.CAPSDACRoleCode.endsWith("View") &&
            !r.CAPSDACRoleCode.endsWith("Edit") &&
            r.CAPSDACRoleCode !== "ADMIN"
        )
        .map((r) => r.CAPSDACRoleCode),
    [orgRoles]
  );

  const adminRole = useMemo(
    () => orgRoles.find((r) => r.CAPSDACRoleCode === "ADMIN"),
    [orgRoles]
  );

  const codeToId = useMemo(
    () =>
      Object.fromEntries(
        (coreData?.securityRoles ?? []).map((r) => [
          r.CAPSDACRoleCode,
          r.CAPSDACRoleId,
        ])
      ),
    [coreData?.securityRoles]
  );

  // Find the current LEA entity to get its esiId — must be declared before existingLeaOrg
  const currentLeaEntity = entityGroup.find(
    (x: any) => x.SecurityGroupKey?.toString() === orgTypeWatch?.toString()
  );

  const idToCode = useMemo(
    () =>
      Object.fromEntries(
        (coreData?.securityRoles ?? []).map((r) => [
          r.CAPSDACRoleId,
          r.CAPSDACRoleCode,
        ])
      ),
    [coreData?.securityRoles]
  );

  const existingLeaOrg = useMemo(() => {
    if (!currentLeaEntity) return null;
    const esiId = currentLeaEntity?.LEAID?.toString() ?? "";
    const expectedPath = `${esiId !== "0" ? "0-" : ""}${esiId}-`;
    return fullUser.userOrgs?.find((o) => o.esiIdPath === expectedPath) ?? null;
  }, [fullUser.userOrgs, currentLeaEntity]);

  const [isOrgAdmin, setIsOrgAdmin] = useState(false);
  const [viewPerms, setViewPerms] = useState<Record<string, boolean>>({});
  const [editPerms, setEditPerms] = useState<Record<string, boolean>>({});
  const [additionalPerms, setAdditionalPerms] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    if (existingLeaOrg) {
      const existingRoleIds: number[] = existingLeaOrg.roleIds ?? [];
      const existingCodes = existingRoleIds
        .map((id) => idToCode[id])
        .filter(Boolean);
      setIsOrgAdmin(
        adminRole ? existingRoleIds.includes(adminRole.CAPSDACRoleId) : false
      );
      setViewPerms(
        Object.fromEntries(
          recordTypes.map((rt) => [rt, existingCodes.includes(`${rt}View`)])
        )
      );
      setEditPerms(
        Object.fromEntries(
          recordTypes.map((rt) => [rt, existingCodes.includes(`${rt}Edit`)])
        )
      );
      setAdditionalPerms(
        Object.fromEntries(
          additionalPermissions.map((p) => [p, existingCodes.includes(p)])
        )
      );
    } else {
      setIsOrgAdmin(false);
      setViewPerms(Object.fromEntries(recordTypes.map((rt) => [rt, false])));
      setEditPerms(Object.fromEntries(recordTypes.map((rt) => [rt, false])));
      setAdditionalPerms(
        Object.fromEntries(additionalPermissions.map((p) => [p, false]))
      );
    }
  }, [
    orgTypeWatch,
    recordTypes,
    additionalPermissions,
    existingLeaOrg,
    idToCode,
    adminRole,
  ]);

  const initialIsOrgAdmin =
    existingLeaOrg && adminRole
      ? (existingLeaOrg.roleIds ?? []).includes(adminRole.CAPSDACRoleId)
      : false;
  const initialViewPerms = Object.fromEntries(
    recordTypes.map((rt) => {
      const codes = (existingLeaOrg?.roleIds ?? [])
        .map((id) => idToCode[id])
        .filter(Boolean);
      return [rt, codes.includes(`${rt}View`)];
    })
  );
  const initialEditPerms = Object.fromEntries(
    recordTypes.map((rt) => {
      const codes = (existingLeaOrg?.roleIds ?? [])
        .map((id) => idToCode[id])
        .filter(Boolean);
      return [rt, codes.includes(`${rt}Edit`)];
    })
  );
  const initialAdditionalPerms = Object.fromEntries(
    additionalPermissions.map((p) => {
      const codes = (existingLeaOrg?.roleIds ?? [])
        .map((id) => idToCode[id])
        .filter(Boolean);
      return [p, codes.includes(p)];
    })
  );

  const isDirty =
    isOrgAdmin !== initialIsOrgAdmin ||
    recordTypes.some(
      (rt) =>
        viewPerms[rt] !== initialViewPerms[rt] ||
        editPerms[rt] !== initialEditPerms[rt]
    ) ||
    additionalPermissions.some(
      (p) => additionalPerms[p] !== initialAdditionalPerms[p]
    );

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  // Build the userOrg object from current role selections.
  const buildUserOrg = (): UserOrgForm | null => {
    const roleIds: string[] = [];
    if (isOrgAdmin && adminRole)
      roleIds.push(adminRole.CAPSDACRoleId.toString());
    recordTypes.forEach((rt) => {
      if (viewPerms[rt] && codeToId[`${rt}View`])
        roleIds.push(codeToId[`${rt}View`].toString());
      if (editPerms[rt] && codeToId[`${rt}Edit`])
        roleIds.push(codeToId[`${rt}Edit`].toString());
    });
    additionalPermissions.forEach((p) => {
      if (additionalPerms[p] && codeToId[p])
        roleIds.push(codeToId[p].toString());
    });

    if (roleIds.length === 0) return null;

    const esiId = currentLeaEntity?.LEAID?.toString() ?? "";
    const x: any = {
      orgType: orgTypeWatch,
      esiId,
      esiIdPath: `${esiId !== "0" ? "0-" : ""}${esiId}-`,
      roleIds,
    };
    return x as UserOrgForm;
  };

  useEffect(() => {
    if (!isNewRecord || !onUserOrgChange) return;
    onUserOrgChange(buildUserOrg());
  }, [
    isNewRecord,
    isOrgAdmin,
    viewPerms,
    editPerms,
    additionalPerms,
    orgTypeWatch,
    currentLeaEntity?.LEAID,
  ]);

  const onSubmit = (x: UserOrgForm) => {
    if (isNewRecord) return; // parent handles submit in new-user mode
    const built = buildUserOrg();
    if (!built) return;
    x.orgType = built.orgType;
    x.esiId = built.esiId;
    x.esiIdPath = built.esiIdPath;
    x.roleIds = built.roleIds;
    userMutateFns.handleUserOrgSave({ ...fullUser, userOrgs: [x] });
  };

  const submitRef = useRef<() => void>(() => {});
  useEffect(() => {
    submitRef.current = handleSubmit(onSubmit);
  });
  useEffect(() => {
    if (isNewRecord) return;
    onRegisterSubmit(() => submitRef.current());
  }, [onRegisterSubmit, isNewRecord]);

  const allView = recordTypes.every((rt) => viewPerms[rt]);
  const allEdit = recordTypes.every((rt) => editPerms[rt]);
  const allAdditional = additionalPermissions.every((p) => additionalPerms[p]);
  const allFullAccess =
    allView && allEdit && (additionalPermissions.length === 0 || allAdditional);

  const toggle = (type: "view" | "edit", rt: string) => {
    if (type === "edit") {
      setEditPerms((prev) => {
        const next = !prev[rt];
        if (next) setViewPerms((vp) => ({ ...vp, [rt]: true }));
        return { ...prev, [rt]: next };
      });
    } else {
      setViewPerms((prev) => {
        const next = !prev[rt];
        if (!next) setEditPerms((ep) => ({ ...ep, [rt]: false }));
        return { ...prev, [rt]: next };
      });
    }
  };

  const setAllView = () => {
    setViewPerms(Object.fromEntries(recordTypes.map((rt) => [rt, !allView])));
    if (allView)
      setEditPerms(Object.fromEntries(recordTypes.map((rt) => [rt, false])));
  };

  const setAllEdit = () => {
    setEditPerms(Object.fromEntries(recordTypes.map((rt) => [rt, !allEdit])));
    if (!allEdit)
      setViewPerms(Object.fromEntries(recordTypes.map((rt) => [rt, true])));
  };

  const setFullAccess = () => {
    setViewPerms(
      Object.fromEntries(recordTypes.map((rt) => [rt, !allFullAccess]))
    );
    setEditPerms(
      Object.fromEntries(recordTypes.map((rt) => [rt, !allFullAccess]))
    );
    setAdditionalPerms(
      Object.fromEntries(additionalPermissions.map((p) => [p, !allFullAccess]))
    );
  };

  const setAllAdditional = () => {
    setAdditionalPerms(
      Object.fromEntries(additionalPermissions.map((p) => [p, !allAdditional]))
    );
  };

  const getDomainLabel = (rt: string) => {
    const label = RECORD_TYPE_LABELS[rt];
    return label ? `${label} (${rt})` : rt;
  };

  const getPermissionLabel = (p: string) =>
    ADDITIONAL_PERMISSION_LABELS[p] ?? p;

  if (!coreData?.securityGroup || !coreData?.securityRoles) return null;

  return (
    <div
      style={{
        borderTop: "1px solid #e5e7eb",
        marginTop: "24px",
        paddingTop: "24px",
      }}
    >
      <div
        className="fw-semibold mb-3"
        style={{
          fontSize: "0.85rem",
          letterSpacing: "0.07em",
          textTransform: "uppercase",
          color: "#475569",
        }}
      >
        Organization Access
      </div>

      <div
        className="d-flex align-items-center gap-3 p-3 mb-4"
        style={{
          background: "rgba(30, 90, 120, 0.06)",
          borderRadius: 8,
          border: "1px solid rgba(30, 90, 120, 0.2)",
          borderLeft: "4px solid var(--color-p3, #1e5a78)",
          fontSize: "14px",
          color: "#1e293b",
        }}
      >
        <LuBuilding2
          size={16}
          style={{ color: "var(--color-p3, #1e5a78)", flexShrink: 0 }}
        />
        <span>
          <span
            className="fw-semibold"
            style={{ color: "var(--color-p3, #1e5a78)" }}
          >
            {currentLeaEntity?.LEAName ?? "Current LEA"}
          </span>
        </span>
      </div>

      {adminRole && (
        <label
          className="d-flex align-items-center gap-3 p-3 mb-4"
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            background: isOrgAdmin ? "rgba(0,58,93,0.03)" : "#fff",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={isOrgAdmin}
            onChange={() => setIsOrgAdmin((v) => !v)}
            style={{
              width: 15,
              height: 15,
              accentColor: "var(--color-p2)",
              flexShrink: 0,
              cursor: "pointer",
            }}
          />
          <LuShield
            size={16}
            style={{
              color: isOrgAdmin ? "var(--color-p2)" : "#94a3b8",
              flexShrink: 0,
            }}
          />
          <div>
            <div
              className="fw-semibold"
              style={{
                fontSize: "0.9rem",
                color: isOrgAdmin ? "var(--color-p2)" : "#1e293b",
              }}
            >
              Administrator
            </div>
            <div className="text-muted" style={{ fontSize: "0.8rem" }}>
              {isOrgAdmin
                ? "Full administrative privileges enabled"
                : "Grant full admin access"}
            </div>
          </div>
        </label>
      )}

      {recordTypes.length > 0 && (
        <div className="mb-4">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <span
              className="fw-semibold"
              style={{
                fontSize: "0.85rem",
                letterSpacing: "0.07em",
                textTransform: "uppercase",
                color: "#475569",
              }}
            >
              Data Access
            </span>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-sm d-flex align-items-center gap-1"
                style={bulkBtnStyle(allFullAccess)}
                onClick={setFullAccess}
              >
                <LuCheck size={11} /> Full Access
              </button>
              <button
                type="button"
                className="btn btn-sm d-flex align-items-center gap-1"
                style={bulkBtnStyle(allEdit)}
                onClick={setAllEdit}
              >
                <LuPencil size={11} /> All Edit
              </button>
              <button
                type="button"
                className="btn btn-sm d-flex align-items-center gap-1"
                style={bulkBtnStyle(allView)}
                onClick={setAllView}
              >
                <LuEye size={11} /> All View
              </button>
              {additionalPermissions.length > 0 && (
                <button
                  type="button"
                  className="btn btn-sm d-flex align-items-center gap-1"
                  style={bulkBtnStyle(allAdditional)}
                  onClick={setAllAdditional}
                >
                  <LuCheck size={11} /> All Additional
                </button>
              )}
            </div>
          </div>

          <div className="row g-2">
            {recordTypes.map((rt) => (
              <div key={rt} className="col-12 col-md-6">
                <div
                  className="d-flex align-items-center justify-content-between px-3"
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                    background: "#fff",
                    height: 40,
                  }}
                >
                  <span
                    className="fw-semibold"
                    style={{ fontSize: "0.85rem", color: "#1e293b" }}
                  >
                    {getDomainLabel(rt)}
                  </span>
                  <div className="d-flex gap-2 align-items-center">
                    {(["view", "edit"] as const).map((type) => {
                      const active =
                        type === "view" ? viewPerms[rt] : editPerms[rt];
                      return (
                        <label
                          key={type}
                          className="d-flex align-items-center gap-1 mb-0"
                          style={{
                            fontSize: "0.82rem",
                            color: active ? "var(--color-p2)" : "#64748b",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={!!active}
                            onChange={() => toggle(type, rt)}
                            style={{
                              accentColor: "var(--color-p2)",
                              width: 15,
                              height: 15,
                              cursor: "pointer",
                            }}
                          />
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {additionalPermissions.length > 0 && (
        <div>
          <div
            className="fw-semibold mb-2"
            style={{
              fontSize: "0.85rem",
              letterSpacing: "0.07em",
              textTransform: "uppercase",
              color: "#475569",
            }}
          >
            Additional Permissions
          </div>
          <div className="d-flex flex-wrap gap-2">
            {additionalPermissions.map((p) => {
              const active = additionalPerms[p];
              return (
                <button
                  key={p}
                  type="button"
                  className="btn d-flex align-items-center gap-2"
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: active ? 600 : 400,
                    borderRadius: 999,
                    padding: "6px 20px",
                    border: `1.5px solid ${active ? "var(--color-p2)" : "#94a3b8"}`,
                    background: active ? "rgba(0,58,93,0.08)" : "#fff",
                    color: active ? "var(--color-p2)" : "#475569",
                    transition: "all 0.15s",
                    lineHeight: 1.4,
                  }}
                  onClick={() =>
                    setAdditionalPerms((prev) => ({ ...prev, [p]: !prev[p] }))
                  }
                >
                  {active && <LuCheck size={13} />}
                  {getPermissionLabel(p)}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
